/**
 * LOKASI SSO Utilities
 *
 * Storage + URL helpers for the LOKASI internal SSO flow (OAuth 2.0
 * authorization code + PKCE against the LOKASI IdP, followed by a
 * `token_exchange` against this app's own BFF).
 */

import { CheckProviderResponse } from "@/types/auth";

import { clearPkceParams } from "./pkceUtils";

const PROVIDER_KEY = "sso_provider";
const LOGOUT_PENDING_KEY = "global_logout_pending";

/** Upper bound on how long a hand-off to the IdP logout endpoint can be in flight. */
const LOGOUT_PENDING_TTL_MS = 60_000;

/**
 * `VITE_BASE_PATH` is authored with a trailing slash in this app
 * (`/intelligence/`), so strip it before concatenating path segments.
 */
export const getBasePath = (): string =>
  (import.meta.env.VITE_BASE_PATH || "").replace(/\/+$/, "");

/**
 * Cache the provider config resolved at `startSso` so the callback can exchange
 * the short-lived `code` immediately, without re-fetching it (which would add
 * latency and risk the code expiring before the exchange).
 */
export const storeProviderConfig = (provider: CheckProviderResponse): void => {
  sessionStorage.setItem(PROVIDER_KEY, JSON.stringify(provider));
};

export const getStoredProviderConfig = (): CheckProviderResponse | null => {
  const raw = sessionStorage.getItem(PROVIDER_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as CheckProviderResponse;
  } catch {
    return null;
  }
};

export const clearProviderConfig = (): void => {
  sessionStorage.removeItem(PROVIDER_KEY);
};

/**
 * Drop everything tied to a single authorization attempt. Runs on both success
 * and failure so a leaked verifier is never reusable: every retry mints a fresh
 * PKCE pair.
 */
export const clearSsoParams = (): void => {
  clearPkceParams();
  clearProviderConfig();
};

/**
 * The SSO callback URL on this origin. Derived from `location.origin` so it
 * matches localhost in dev and the deployed origin in production — but every
 * origin must be registered with the IdP.
 */
export const getSsoCallbackUrl = (): string =>
  `${globalThis.location.origin}${getBasePath()}/sso/callback`;

/**
 * The landing page a completed global logout returns to — both as the
 * `post_logout_redirect_uri` we hand the IdP and as our own fallback when the
 * IdP never gave us a logout endpoint to visit.
 */
export const getLoggedOutUrl = (): string =>
  `${globalThis.location.origin}${getBasePath()}/logged-out`;

/**
 * The LOKASI auth app (login UI) always lives at `/auth` on the current origin,
 * partner subdomains included.
 */
export const getAuthAppUrl = (): string => `${globalThis.location.origin}/auth`;

/**
 * Point the IdP's logout endpoint back at our `/logged-out` landing page, so
 * the browser returns here once the SSO session is actually gone rather than
 * stopping on the IdP. Both origins must agree on this URL: the IdP only honours
 * a `post_logout_redirect_uri` it has registered.
 *
 * Returns `null` when there is nothing usable to navigate to. The URL is
 * server-supplied, and the only caller is a mutation success handler where a
 * throw would surface as an unhandled rejection instead of an error toast.
 */
export const getRedirectUrlAfterLogout = (
  idpLogoutUrl?: string
): string | null => {
  if (!idpLogoutUrl) return null;

  try {
    const url = new URL(idpLogoutUrl);
    url.searchParams.set("post_logout_redirect_uri", getLoggedOutUrl());
    return url.toString();
  } catch {
    return null;
  }
};

/**
 * Global logout has to reset this app's state *and* hand the browser to the
 * IdP, and the reset lands first: it re-renders the app as unauthenticated,
 * which is exactly the condition `useSsoAutoStart` reacts to by navigating to
 * `/authorize`. That navigation is assigned after ours and would win the race,
 * landing on an IdP session that is still alive — a silent re-login that undoes
 * the logout.
 *
 * So the intent is parked before the reset and read synchronously during render,
 * which is early enough for auto-start to never arm. It lives in `sessionStorage`
 * because the document is about to be replaced; the TTL is there so a logout that
 * never completes (IdP unreachable, user hits Back) can't disable SSO for the
 * rest of the tab's life.
 */
export const markGlobalLogoutPending = (): void => {
  sessionStorage.setItem(LOGOUT_PENDING_KEY, String(Date.now()));
};

export const isGlobalLogoutPending = (): boolean => {
  const startedAt = Number(sessionStorage.getItem(LOGOUT_PENDING_KEY));

  return (
    Number.isFinite(startedAt) &&
    startedAt > 0 &&
    Date.now() - startedAt < LOGOUT_PENDING_TTL_MS
  );
};

export const clearGlobalLogoutPending = (): void => {
  sessionStorage.removeItem(LOGOUT_PENDING_KEY);
};

/**
 * Anti open-redirect control. The callback's `redirect_to` arrives from the URL,
 * so it must be proven same-origin before any navigation is built from it.
 */
export const isSameOriginUrl = (url: string): boolean => {
  try {
    return (
      new URL(url, globalThis.location.origin).origin ===
      globalThis.location.origin
    );
  } catch {
    return false;
  }
};
